import React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { routePaths } from 'router';
import { useCreateOrder } from 'api/orders/use-create-order.ts';
import type { TCreateOrderReq } from 'api/swagger';
import { UserRole, useRoleGuard } from 'shared/hooks/use-role-guard';
import { FormDrawerWithButton } from 'components/common/form-drawer-with-button';
import type { IOrderFormFields } from 'components/orders/order-form';
import { OrderForm } from 'components/orders/order-form';
import { OrderFormConclusion } from 'components/orders/order-form/components/order-form-conclusion';

export const OrderCreate: React.FC = () => {
  const isAllow = useRoleGuard([UserRole.admin, UserRole.assistant, UserRole.customer_manager]);
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { mutateAsync: createOrder, isLoading } = useCreateOrder();

  const onFinish = async (values: IOrderFormFields) => {
    const apiVariables: TCreateOrderReq = {
      clientOrderNumber: values.clientOrderNumber,
      clientId: values.client.value,
      contactId: values.contact.value,
      currency: values.currency,
      freight: values.freight,
      documentPrice: values.documentPrice,
      paymentTerms: values.paymentTerms,
      paymentPeriod: values.paymentPeriod,
    };

    const result = await createOrder(apiVariables);
    navigate(generatePath(routePaths.order, { orderId: String(result.id) }));
  };

  if (!isAllow) return null;

  return (
    <FormDrawerWithButton
      formComponent={OrderForm}
      formProps={{
        footerRender: (client, contact) => <OrderFormConclusion client={client} contact={contact} />,
      }}
      title={t('order:create_order')}
      buttonLabel={t('order:add_order')}
      isLoading={isLoading}
      onFinish={onFinish}
    />
  );
};
